import { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Badge,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import styled from '@emotion/styled';
import { getColor } from '../../../../theme/colors/colors';

export type ColumnKey = 'ssp' | 'buyerSeatName' | 'buyerSeatId' | 'dealId' | 'name' | 'floorPrice' | 'mediaType' | 'status' | 'created' | 'actions';

export interface ColumnConfig {
  key: ColumnKey;
  label: string;
}

const AVAILABLE_COLUMNS: ColumnConfig[] = [
  { key: 'ssp', label: 'SSP' },
  { key: 'buyerSeatName', label: 'Buyer Seat/Member' },
  { key: 'buyerSeatId', label: 'Buyer ID' },
  { key: 'dealId', label: 'Deal ID' },
  { key: 'name', label: 'Deal Name' },
  { key: 'floorPrice', label: 'Floor Price' },
  { key: 'mediaType', label: 'Media Type' },
  { key: 'status', label: 'Status' },
  { key: 'created', label: 'Created' },
  { key: 'actions', label: 'Actions' },
];

type ColumnVisibilityButtonProps = {
  visibleColumns: ColumnKey[];
  onColumnVisibilityChange: (columns: ColumnKey[]) => void;
};

const ColumnVisibilityButton = ({
  visibleColumns,
  onColumnVisibilityChange,
}: ColumnVisibilityButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (columnKey: ColumnKey) => {
    const newVisibleColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    onColumnVisibilityChange(newVisibleColumns);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'column-visibility-popover' : undefined;

  const selectedCount = visibleColumns.length;
  const totalCount = AVAILABLE_COLUMNS.length;

  return (
    <>
      <StyledButton
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        startIcon={<VisibilityIcon />}
        endIcon={
          <StyledBadge >
            {selectedCount}/{totalCount}
          </StyledBadge>
        }
      >
        Columns
      </StyledButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          <FormGroup>
            {AVAILABLE_COLUMNS.map((column) => {
              const isChecked = visibleColumns.includes(column.key);
              return (
                <Box key={column.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() => handleToggleColumn(column.key)}
                        size="small"
                      />
                    }
                    label={column.label}
                    sx={{ flex: 1, mr: 0 }}
                  />
                  {isChecked && (
                    <CheckIcon sx={{ color: 'primary.main', mr: 1 }} fontSize="small" />
                  )}
                </Box>
              );
            })}
          </FormGroup>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default ColumnVisibilityButton;

const StyledButton = styled(Button)`
  border-color: rgba(0, 0, 0, 0.23);
  color: rgba(0, 0, 0, 0.87);
  text-transform: none;
  font-weight: 400;
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.23);
  padding: 8.5px 14px;
  height: 40px;
  margin-top: 4px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.23);
    border: 1px solid rgba(0, 0, 0, 0.23);
  }

  .MuiButton-startIcon {
    margin-right: 8px;
  }

  .MuiButton-endIcon {
    margin-left: 8px;
  }
`;

const StyledBadge = styled(Box)`
  background-color: ${getColor('banner')};
  color: ${getColor('bannerContrast')};
  border-radius: 0;
  padding: 2px 12px;
  font-size: 14px!important;
  font-weight: 500!important;
  line-height: 16px;
  min-width: 50px;
  text-align: center;
`;

const PopoverContent = styled.div`
  padding: 16px;
  min-width: 280px;

  .MuiFormControlLabel-root {
    margin-left: 0;
    margin-right: 0;
  }

  .MuiFormControlLabel-label {
    font-size: 14px;
  }
`;
